/**
 * ConfirmationStep — Confirmation finale avant exécution du retrait
 */

import { useState } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  User,
  CreditCard,
  FileText,
  ArrowLeft,
  Lock,
  Banknote,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { formatAmount, maskAccountNumber, WITHDRAWAL_THRESHOLDS } from '../types';

interface ExternalRecipientInfo {
  name: string;
  phone: string;
  idNumber: string;
}

interface ConfirmationStepProps {
  accountNumber: string;
  holderName: string;
  amount: number;
  denomination: Map<number, number>;
  destination: 'self' | 'external';
  externalRecipient?: ExternalRecipientInfo;
  notes?: string;
  currentBalance: number;
  onConfirm: () => void;
  onModify: () => void;
  loading?: boolean;
}

const BILL_LABELS: Record<number, string> = {
  10000: '10 000',
  5000: '5 000',
  2000: '2 000',
  1000: '1 000',
  500: '500',
};

const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];
const GREEN_GRAD = 'linear-gradient(to bottom right, #016951, #014a3a)';

function SectionTitle({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontWeight: 700,
        fontSize: '0.82rem',
        color: '#64748b',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
        marginBottom: 10,
      }}
    >
      {icon}
      {label}
    </div>
  );
}

export function ConfirmationStep({
  accountNumber,
  holderName,
  amount,
  denomination,
  destination,
  externalRecipient,
  notes,
  currentBalance,
  onConfirm,
  onModify,
  loading = false,
}: ConfirmationStepProps) {
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const isHighAmount = amount >= WITHDRAWAL_THRESHOLDS.highAmountWarning;
  const requiresSupervisor = amount >= WITHDRAWAL_THRESHOLDS.supervisorRequired;

  const billsList = Array.from(denomination.entries())
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[0] - a[0]);

  const handleConfirmClick = () => {
    if (!hasConfirmed) {
      setHasConfirmed(true);
      return;
    }
    onConfirm();
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div
        style={{
          background: 'white',
          borderRadius: 20,
          boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
          border: '1px solid rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            background: 'linear-gradient(135deg,#92400e,#b45309)',
            padding: '18px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <div
            style={{
              padding: 10,
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 10,
            }}
          >
            <AlertTriangle style={{ width: 20, height: 20, color: 'white' }} />
          </div>
          <div>
            <h3 style={{ fontWeight: 800, fontSize: '1rem', color: 'white' }}>
              Confirmation du retrait
            </h3>
            <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
              Vérifiez les informations avant de valider
            </p>
          </div>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* ── Montant principal ── */}
          <motion.div
            initial={{ scale: 0.96 }}
            animate={{ scale: 1 }}
            transition={{ ease: EASE_OUT }}
            style={{
              textAlign: 'center',
              padding: '20px 16px',
              background: '#f8fafc',
              borderRadius: 16,
              border: '1px solid #f1f5f9',
            }}
          >
            <p style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>
              Montant à retirer
            </p>
            <p style={{ fontSize: '2.4rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
              {formatAmount(amount)}
            </p>
            {isHighAmount && (
              <p
                style={{
                  marginTop: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: '0.78rem',
                  color: '#d97706',
                  fontWeight: 700,
                }}
              >
                <AlertTriangle style={{ width: 14, height: 14 }} />
                Montant élevé
                {requiresSupervisor && ' — Validation superviseur requise'}
              </p>
            )}
          </motion.div>

          {/* ── Compte débité ── */}
          <div>
            <SectionTitle
              icon={<CreditCard style={{ width: 14, height: 14 }} />}
              label="Compte débité"
            />
            <div
              style={{
                padding: '12px 16px',
                background: '#f8fafc',
                borderRadius: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <p style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
                  {holderName}
                </p>
                <p
                  style={{
                    fontSize: '0.78rem',
                    color: '#64748b',
                    fontFamily: 'monospace',
                    marginTop: 2,
                  }}
                >
                  {maskAccountNumber(accountNumber)}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>
                  Solde actuel
                </p>
                <p style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
                  {formatAmount(currentBalance)}
                </p>
              </div>
            </div>
          </div>

          {/* ── Bénéficiaire ── */}
          <div>
            <SectionTitle
              icon={<User style={{ width: 14, height: 14 }} />}
              label="Bénéficiaire"
            />
            <div
              style={{
                padding: '12px 16px',
                background: '#f8fafc',
                borderRadius: 12,
              }}
            >
              {destination === 'self' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <User style={{ width: 16, height: 16, color: '#16a34a' }} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
                      Retrait personnel
                    </p>
                    <p style={{ fontSize: '0.78rem', color: '#64748b' }}>
                      Le titulaire du compte
                    </p>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: '#fef3c7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <User style={{ width: 16, height: 16, color: '#d97706' }} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
                      Tiers : {externalRecipient?.name}
                    </p>
                    <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                      Tél : {externalRecipient?.phone}
                    </p>
                    <p style={{ fontSize: '0.78rem', color: '#64748b' }}>
                      Pièce : {externalRecipient?.idNumber}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Billetage ── */}
          {billsList.length > 0 && (
            <div>
              <SectionTitle
                icon={<Banknote style={{ width: 14, height: 14 }} />}
                label="Détail des billets"
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                  gap: 8,
                }}
              >
                {billsList.map(([value, count]) => (
                  <div
                    key={value}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: '#f8fafc',
                      borderRadius: 10,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <span style={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>
                      {BILL_LABELS[value] || value} FCFA
                    </span>
                    <span
                      style={{
                        fontSize: '0.82rem',
                        fontWeight: 800,
                        color: '#1e293b',
                        background: '#e0e7ff',
                        borderRadius: 6,
                        padding: '2px 8px',
                      }}
                    >
                      × {count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Notes ── */}
          {notes && (
            <div>
              <SectionTitle
                icon={<FileText style={{ width: 14, height: 14 }} />}
                label="Notes"
              />
              <p
                style={{
                  padding: '10px 14px',
                  background: '#f8fafc',
                  borderRadius: 12,
                  color: '#374151',
                  fontSize: '0.875rem',
                }}
              >
                {notes}
              </p>
            </div>
          )}

          {/* ── Avertissement ── */}
          <div
            style={{
              padding: '12px 16px',
              background: '#fffbeb',
              border: '1.5px solid #fde68a',
              borderRadius: 14,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <AlertTriangle
              style={{ width: 16, height: 16, color: '#d97706', flexShrink: 0, marginTop: 1 }}
            />
            <div style={{ fontSize: '0.8rem', color: '#92400e' }}>
              <p style={{ fontWeight: 700, marginBottom: 3 }}>Attention</p>
              <p>
                Une fois validé, ce retrait ne pourra pas être annulé. Vérifiez que toutes les
                informations sont correctes.
              </p>
            </div>
          </div>
        </div>

        {/* ── Actions ── */}
        <div
          style={{
            padding: '0 24px 24px',
            display: 'flex',
            gap: 12,
          }}
        >
          <button
            onClick={onModify}
            disabled={loading}
            style={{
              flex: 1,
              height: 50,
              background: 'white',
              border: '1.5px solid #e2e8f0',
              borderRadius: 14,
              color: '#374151',
              fontWeight: 700,
              fontSize: '0.875rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'border-color 0.2s',
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            Modifier
          </button>

          <motion.button
            onClick={handleConfirmClick}
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: 0.97 }}
            style={{
              flex: 2,
              height: 50,
              background: hasConfirmed
                ? 'linear-gradient(135deg,#22c55e,#16a34a)'
                : GREEN_GRAD,
              border: 'none',
              borderRadius: 14,
              color: 'white',
              fontWeight: 700,
              fontSize: '0.875rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: hasConfirmed
                ? '0 6px 20px rgba(34,197,94,0.35)'
                : '0 6px 20px rgba(37,99,235,0.3)',
              transition: 'background 0.25s, box-shadow 0.25s',
            }}
          >
            {loading ? (
              <>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    border: '2.5px solid rgba(255,255,255,0.4)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin .75s linear infinite',
                    display: 'inline-block',
                  }}
                />
                Traitement...
              </>
            ) : hasConfirmed ? (
              <>
                <Lock style={{ width: 16, height: 16 }} />
                VALIDER DÉFINITIVEMENT
              </>
            ) : (
              <>
                <CheckCircle style={{ width: 16, height: 16 }} />
                Je confirme les informations
              </>
            )}
          </motion.button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
